/**
 * PostConsultCertificate — Paso opcional tras crear la receta.
 *
 * Ema (asistente de IA) le pregunta al doctor si desea emitir un certificado
 * médico para el paciente. Si acepta, se muestra un formulario editable con
 * el diagnóstico pre-llenado; el POST /api/certificates lo persiste y
 * redirige a la vista imprimible. Si no, se avanza a "siguiente cita".
 */
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  ArrowRight,
  X,
  ShieldCheck,
  Sparkles,
  Image as ImageIcon,
  PenLine,
  Calendar as CalendarIcon,
} from "@/components/icons";

// Helper: nombre formal del doctor para saludo
// eslint-disable-next-line no-unused-vars
function useDoctorAddress() {
  const { user } = useAuth();
  const raw = (user?.name || "").trim();
  if (!raw) return "doctor";
  const first = raw.split(/\s+/)[0];
  if (/^(dr|dra|doctor|doctora)\.?$/i.test(first))
    return raw.split(/\s+/).slice(0, 2).join(" ");
  return `Dr. ${first}`;
}

export default function PostConsultCertificate() {
  const { appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const patientId = location.state?.patientId;
  const patientName = location.state?.patientName || "Paciente";
  const diagnosis = location.state?.diagnosis || "";
  const followUp = location.state?.followUp || "";

  const [phase, setPhase] = useState("ask"); // ask | edit
  const [saving, setSaving] = useState(false);

  // Campos editables — pre-llenados
  const [dx, setDx] = useState(diagnosis);
  const [note, setNote] = useState(
    "SE RECOMIENDA REPOSO EN CASA POR 3 DÍAS CON FINES DE TRATAMIENTO Y MEJORÍA.",
  );
  const [reason, setReason] = useState("a petición del interesado");
  const [place, setPlace] = useState("");
  const [dateIso, setDateIso] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  // Toggles — usan las imágenes que el doctor tenga en su perfil.
  // No se puede subir aquí; sólo decidir si se incluye o no en este cert.
  const [useLogo, setUseLogo] = useState(true);
  const [useStamp, setUseStamp] = useState(true);
  const [useSignature, setUseSignature] = useState(true);

  // Autocompletar nota con Ema (multi-variante)
  const [suggesting, setSuggesting] = useState(false);
  const [noteHistory, setNoteHistory] = useState([]); // notas que Ema ya sugirió
  const suggestNote = async () => {
    const d = dx.trim();
    if (d.length < 3) {
      toast.error("Escribe el diagnóstico primero para que Ema sugiera la nota.");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/ai-consultation/certificate-note", {
        diagnosis: d,
        patient_name: patientName,
        previous_notes: noteHistory,
      });
      if (data?.note) {
        setNote(data.note);
        setNoteHistory((prev) => [...prev, data.note]);
        toast.success(
          noteHistory.length === 0
            ? "Ema sugirió una nota — revísala antes de emitir."
            : `Variante ${noteHistory.length + 1} lista.`,
        );
      }
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "No se pudo sugerir la nota.");
    } finally {
      setSuggesting(false);
    }
  };

  const goToNextStep = () =>
    navigate(`/doctor/consulta/${appointmentId}/siguiente-cita`, {
      state: { patientId, patientName, followUp },
      replace: true,
    });

  const submit = async () => {
    if (!dx.trim()) {
      toast.error("El diagnóstico del certificado es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/certificates", {
        patient_id: patientId,
        appointment_id: appointmentId,
        diagnosis: dx.trim(),
        note: note.trim(),
        place: place.trim() || undefined,
        reason: reason.trim() || undefined,
        date_iso: dateIso,
        // El backend usa las URLs del perfil por default; enviamos null
        // explícito para quitar el elemento en este certificado puntual.
        clinic_logo_url: useLogo ? (user?.clinic_logo_url || null) : null,
        signature_url: useSignature ? (user?.signature_url || null) : null,
        stamp_url: useStamp ? (user?.stamp_url || null) : null,
      });
      toast.success("Certificado emitido");
      // Navegar a la vista imprimible, y guardar el estado para continuar el flujo después
      navigate(`/doctor/certificados/${data.id}`, {
        state: {
          returnTo: `/doctor/consulta/${appointmentId}/siguiente-cita`,
          returnState: { patientId, patientName, followUp },
        },
      });
    } catch (err) {
      toast.error(
        "No pude emitir el certificado: " + formatErr(err?.response?.data?.detail),
      );
      setSaving(false);
    }
  };

  return (
    <div className="fade-up max-w-3xl mx-auto">
      {phase === "ask" ? (
        <div
          className="fade-scale-in relative py-10 sm:py-12 text-center"
          data-testid="cert-ask-card"
        >
          <button
            onClick={goToNextStep}
            data-testid="cert-skip-x"
            aria-label="Omitir certificado"
            className="absolute top-2 right-2 w-8 h-8 rounded-xl bg-white hover:bg-black/[0.04] border border-black/[0.06] flex items-center justify-center transition"
          >
            <X className="w-3.5 h-3.5 text-black/60" />
          </button>

          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#0071E3] flex items-center justify-center mb-5">
            <ShieldCheck
              className="w-7 h-7 text-white"
              strokeWidth={1.75}
              weight="regular"
            />
          </div>

          <p className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-2">
            Paso opcional
          </p>
          <h2
            className="text-[26px] sm:text-[28px] font-semibold text-black tracking-tight leading-tight"
            data-testid="cert-ask-title"
          >
            ¿Emitir certificado médico?
          </h2>
          <p className="text-[14px] text-black/55 mt-2 max-w-md mx-auto leading-relaxed">
            La receta de{" "}
            <span className="font-semibold text-black">{patientName}</span>{" "}
            quedó registrada. Puedes emitir también un certificado médico
            ahora o continuar al siguiente paso.
          </p>

          <div className="mt-7 flex items-center justify-center gap-2.5 flex-wrap">
            <button
              type="button"
              onClick={goToNextStep}
              data-testid="cert-skip"
              className="h-11 inline-flex items-center gap-1.5 bg-white hover:bg-black/[0.03] text-black text-[13px] font-semibold rounded-full px-5 border border-black/[0.08] transition"
            >
              No, continuar
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
            </button>
            <button
              type="button"
              onClick={() => setPhase("edit")}
              data-testid="cert-accept"
              className="h-11 inline-flex items-center bg-black hover:bg-black/85 text-white text-[13px] font-semibold rounded-full px-5 transition"
            >
              Sí, crear certificado
            </button>
          </div>
        </div>
      ) : (
        <div className="fade-scale-in bg-white rounded-[28px] border border-black/[0.06] p-6 sm:p-8 relative">
            <button
              onClick={goToNextStep}
              data-testid="cert-skip-x"
              aria-label="Omitir certificado"
              className="absolute top-5 right-5 w-8 h-8 rounded-xl bg-black/[0.04] hover:bg-black/[0.08] flex items-center justify-center transition"
            >
              <X className="w-3.5 h-3.5 text-black/70" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-[#0071E3] flex items-center justify-center">
                <ShieldCheck
                  className="w-5 h-5 text-white"
                  strokeWidth={1.75}
                  weight="regular"
                />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                  Certificado médico
                </p>
                <h2 className="text-[22px] font-semibold text-black leading-tight tracking-tight">
                  Revisa el contenido
                </h2>
                <p className="text-[13px] text-black/55 mt-0.5">
                  Paciente:{" "}
                  <span className="font-semibold text-black">{patientName}</span>
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Toolbar — toggles del membrete + fecha editable.
               * Las imágenes vienen del perfil del doctor (Configuración →
               * Marca). Si un elemento no está subido en el perfil, el
               * toggle aparece deshabilitado con un aviso. */}
              <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-black/[0.06]">
                <MediaToggle
                  label="Logo"
                  icon={ImageIcon}
                  active={useLogo}
                  hasSource={Boolean(user?.clinic_logo_url)}
                  onToggle={() => setUseLogo((v) => !v)}
                  testid="cert-toolbar-logo"
                />
                <MediaToggle
                  label="Sello"
                  icon={ImageIcon}
                  active={useStamp}
                  hasSource={Boolean(user?.stamp_url)}
                  onToggle={() => setUseStamp((v) => !v)}
                  testid="cert-toolbar-stamp"
                />
                <MediaToggle
                  label="Firma"
                  icon={PenLine}
                  active={useSignature}
                  hasSource={Boolean(user?.signature_url)}
                  onToggle={() => setUseSignature((v) => !v)}
                  testid="cert-toolbar-signature"
                />
                <label className="relative inline-flex items-center gap-1.5 bg-black/[0.04] hover:bg-black/[0.08] rounded-full px-3 py-1.5 cursor-pointer transition text-xs font-bold text-black/75">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <span>{new Date(dateIso + "T00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <input
                    type="date"
                    value={dateIso}
                    onChange={(e) => e.target.value && setDateIso(e.target.value)}
                    data-testid="cert-toolbar-date"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
              </div>

              {/* Aviso si falta el membrete en el perfil */}
              {(!user?.clinic_logo_url || !user?.stamp_url || !user?.signature_url) && (
                <button
                  type="button"
                  onClick={() => navigate("/perfil?tab=brand")}
                  data-testid="cert-brand-missing-cta"
                  className="w-full text-left bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-2xl px-4 py-3 text-xs text-amber-900 leading-relaxed transition group"
                >
                  <span className="font-bold">⚠️ Configura tu membrete</span>
                  <span className="block mt-0.5">
                    Falta subir{" "}
                    <span className="font-bold">
                      {[
                        !user?.clinic_logo_url && "logo",
                        !user?.stamp_url && "sello",
                        !user?.signature_url && "firma",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    . Haz click aquí para subirlo ahora en tu perfil →
                  </span>
                </button>
              )}

              <FieldBlock label="Paciente">
                <input
                  type="text"
                  value={patientName}
                  disabled
                  className="w-full bg-[#F5F5F5] rounded-2xl px-4 py-2.5 text-sm text-black/70"
                />
              </FieldBlock>

              <FieldBlock label="Diagnóstico">
                <input
                  type="text"
                  value={dx}
                  onChange={(e) => setDx(e.target.value)}
                  data-testid="cert-dx"
                  placeholder="Ej. COVID-19 POSITIVO"
                  className="w-full bg-white rounded-2xl px-4 py-2.5 text-sm border border-black/10 focus:border-[#01BC8E] focus:outline-none"
                />
              </FieldBlock>

              <div className="block">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-black/55">
                    Nota / indicaciones
                  </span>
                  <button
                    type="button"
                    onClick={suggestNote}
                    disabled={suggesting || dx.trim().length < 3}
                    data-testid="cert-note-suggest"
                    title={
                      dx.trim().length < 3
                        ? "Escribe primero el diagnóstico"
                        : noteHistory.length > 0
                          ? "Ema redactará una variante distinta"
                          : "Ema redactará una nota basada en el diagnóstico"
                    }
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0071E3] hover:text-[#0062c4] disabled:text-black/30 disabled:cursor-not-allowed transition"
                  >
                    <Sparkles
                      className={`w-3.5 h-3.5 ${suggesting ? "animate-pulse" : ""}`}
                      strokeWidth={1.75}
                      weight="regular"
                    />
                    {suggesting
                      ? "Ema está escribiendo…"
                      : noteHistory.length > 0
                        ? "Otra variante"
                        : "Sugerir con Ema"}
                  </button>
                </div>
                <div className="mt-1">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="cert-note"
                    rows={3}
                    className="w-full bg-white rounded-2xl px-4 py-2.5 text-sm border border-black/10 focus:border-[#01BC8E] focus:outline-none resize-y"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldBlock label="Lugar de emisión (opcional)">
                  <input
                    type="text"
                    value={place}
                    onChange={(e) => setPlace(e.target.value)}
                    data-testid="cert-place"
                    placeholder="Se toma del perfil si se deja vacío"
                    className="w-full bg-white rounded-2xl px-4 py-2.5 text-sm border border-black/10 focus:border-[#01BC8E] focus:outline-none"
                  />
                </FieldBlock>
                <FieldBlock label="Motivo">
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="cert-reason"
                    className="w-full bg-white rounded-2xl px-4 py-2.5 text-sm border border-black/10 focus:border-[#01BC8E] focus:outline-none"
                  />
                </FieldBlock>
              </div>

              {/* Resumen pre-firma — recordatorio de lo que se emitirá */}
              <div
                className="bg-[#F5F5F7] rounded-2xl p-4 border border-black/[0.04]"
                data-testid="cert-summary"
              >
                <p className="text-[10px] uppercase tracking-widest font-bold text-black/55 mb-2">
                  Revisa antes de emitir
                </p>
                <dl className="space-y-1.5 text-[13px] leading-relaxed">
                  <div className="flex gap-2">
                    <dt className="text-black/45 w-24 shrink-0">Paciente</dt>
                    <dd className="text-black font-medium">{patientName}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-black/45 w-24 shrink-0">Diagnóstico</dt>
                    <dd
                      className={`font-medium ${dx.trim() ? "text-black" : "text-red-600"}`}
                      data-testid="cert-summary-dx"
                    >
                      {dx.trim() || "— falta completar —"}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-black/45 w-24 shrink-0">Nota</dt>
                    <dd
                      className="text-black/80 line-clamp-2"
                      data-testid="cert-summary-note"
                    >
                      {note.trim() || "— sin indicaciones —"}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-black/45 w-24 shrink-0">Fecha</dt>
                    <dd className="text-black/80">
                      {new Date(dateIso + "T00:00").toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="pt-2 flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={goToNextStep}
                  data-testid="cert-cancel"
                  className="inline-flex items-center gap-1.5 bg-white text-black text-xs font-bold rounded-xl px-4 py-2 border border-black/10 hover:bg-black/[0.03] transition"
                >
                  Cancelar y continuar
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={saving}
                  data-testid="cert-submit"
                  className="btn-primary"
                >
                  {saving ? "Emitiendo..." : "Emitir certificado"}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

function FieldBlock({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold text-black/55">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/**
 * MediaToggle — chip para activar/desactivar un elemento del membrete
 * (logo, sello, firma). No sube archivos; sólo controla si se usa lo que
 * el doctor ya tiene subido en su perfil. Deshabilitado si aún no lo subió.
 */
function MediaToggle({ label, icon: Icon, active, hasSource, onToggle, testid }) {
  const disabled = !hasSource;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      data-testid={testid}
      title={
        disabled
          ? `Sube ${label.toLowerCase()} en tu perfil primero`
          : active
            ? `${label} activo — click para quitar de este certificado`
            : `${label} desactivado — click para incluirlo`
      }
      className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition ${
        disabled
          ? "bg-black/[0.03] text-black/30 cursor-not-allowed"
          : active
            ? "bg-[#01BC8E]/15 text-[#016649] hover:bg-[#01BC8E]/25"
            : "bg-black/[0.04] text-black/50 hover:bg-black/[0.08]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {!disabled && (
        <span className="text-[10px] opacity-80">{active ? "✓" : "○"}</span>
      )}
    </button>
  );
}
